import React, { useState } from 'react';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Employee } from '../../types/Employee';
import { Avatar, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, FormHelperText, IconButton, Input, InputAdornment, InputLabel, Stack, Typography } from '@mui/material';
import { VisibilityOff, Visibility } from '@mui/icons-material';

type EmployeeResetUserAccountProps = {
  employee: Employee
}; 
export const EmployeeResetUserAccount = (props: EmployeeResetUserAccountProps) => {
  const { employee } = props;

  const defaultPass =  `A${employee.idNumber}a`;
  const [password, setPassword] = useState(defaultPass);
  const [repass, setRepass] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showRetypePassword, setShowRetypePassword] = useState(false);
  const [useDefaultPassword, setUseDefaultPassword] = useState(true);

  const [loading, setLoading] = useState(false);

  const handlePasswordOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.currentTarget.value;
    setPassword(text);
    setUseDefaultPassword(text === defaultPass);
  };

  const handleRetypePasswordOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.currentTarget.value;
    setRepass(text);
  };

  const handleShowPasswordOnClick = () => setShowPassword((show) => !show);
  const handleShowRetypePasswordOnClick = () => setShowRetypePassword((show) => !show);

  const handleUseDefaultPasswordOnChange = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setPassword(checked ? defaultPass : '');
    setRepass('');
    setUseDefaultPassword(checked);
  };

  const handleResetAccountOnClick = () => {
    if (loading) return;

    if (password.trim() === '') {
      window.alert('Please enter password!');
      return;
    }

    if (!useDefaultPassword) {
      if (password.trim() !== repass.trim()) {
        window.alert('Password does not  match!');
        return;
      }
    }

    if (!window.confirm(`Are you sure you want to reset user's  Account?`))
      return;

    setLoading(true);

    const functions = getFunctions();
    const updatePasswordByUid = httpsCallable(functions, 'updatePasswordByUid');

    const uid = employee.uid;
    updatePasswordByUid({ uid, password }).then(userRecord => {
      window.alert(`Employee ${employee.idNumber} account has been reset.`);
      setLoading(false);
    });
  };

  return (
    <Box>
      <Box mb={2}>
        <Stack direction='row' spacing={2}>
          <Avatar src={employee.picture} />
          <Stack>
            <Typography fontWeight='700'>{employee.name}</Typography>
            <Typography variant='caption'>{employee.idNumber}</Typography>
          </Stack>
        </Stack>
      </Box>
      <FormGroup>
        <FormControl sx={{ width: '25ch' }} variant="standard" fullWidth>
          <InputLabel htmlFor="standard-adornment-password">
            {useDefaultPassword ? 'Password' : 'Enter Password'}
          </InputLabel>
          <Input
            id="standard-adornment-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordOnChange}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleShowPasswordOnClick}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
          />
        </FormControl>
        {!useDefaultPassword && (
          <FormControl error={password !== repass && repass.trim() !== ''} sx={{ mt: 2, width: '25ch' }} variant="standard" fullWidth>
            <InputLabel htmlFor="standard-adornment-password">
              Retype Password
            </InputLabel>
            <Input
              id="standard-adornment-password"
              type={showRetypePassword ? 'text' : 'password'}
              value={repass}
              onChange={handleRetypePasswordOnChange}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleShowRetypePasswordOnClick}
                  >
                    {showRetypePassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              fullWidth
            />
            <FormHelperText id="component-error-text">
              {password !== repass && repass.trim() !== '' ? 'Password does not match' : ''}
            </FormHelperText>
          </FormControl>
        )}
        <FormControlLabel
          sx={{ mt: 2 }}
          label="Use default password"
          control={<Checkbox checked={useDefaultPassword} size='small' onChange={handleUseDefaultPasswordOnChange} />}
        />
      </FormGroup>
      <Box mt={2}>
        <Stack direction='row-reverse'>
          <Button variant='contained' disabled={loading} onClick={handleResetAccountOnClick}>
            Reset Account
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
